import ClassicExtension from '../../ClassicExtension';
import { easepick } from '@easepick/bundle';
import { getEntryPaths } from '../../../helpers/encoreEntrypoints';
import moment from 'moment';

/**
 * Date inputs
 *  1. text inputs are renamed to _dateFrom and _dateTo (front-end inputs)
 *  2. hidden inputs keep dateFrom and dateTo names (back-end inputs)
 *  3. each text input has its own single-date picker
 */
export default class DateRangePickerExtension extends ClassicExtension {

	private readonly dateFormatFE = 'D.M.YYYY';
	private readonly dateFormatBE = 'YYYY-MM-DD';

	private pickers: any[] = [];

	protected init($root: JQuery): void {
		const selector = '[data-date-range]:not([data-date-range-initialized])';
		const $dateRangePickers = $root.filter(selector).add($root.find(selector));

		$dateRangePickers.each((i, dateFromElement) => {
			const $dateFrom = $(dateFromElement);
			const dateToSelector = $dateFrom.data('date-range-to');
			const $dateTo = $root.filter(dateToSelector).add($root.find(dateToSelector)).first();

			if ($dateTo.length === 0) {
				return;
			}

			$dateFrom.attr('data-date-range-initialized', 'true');

			const fromValue = String($dateFrom.val() ?? '');
			const toValue = String($dateTo.val() ?? '');
			const dateFromName = $dateFrom.attr('name') ?? '';
			const dateToName = $dateTo.attr('name') ?? '';

			$dateFrom
				.attr('name', '_' + dateFromName)
				.val(this.formatBackendDateForDisplay(fromValue));
			$dateTo
				.attr('name', '_' + dateToName)
				.val(this.formatBackendDateForDisplay(toValue));

			const $hiddenDateFrom = $(`<input type="hidden" name="${dateFromName}" value="${fromValue}">`);
			const $hiddenDateTo = $(`<input type="hidden" name="${dateToName}" value="${toValue}">`);
			$dateFrom.after($hiddenDateFrom);
			$dateTo.after($hiddenDateTo);

			$dateFrom.on('keydown', e => {
				if (e.key === 'Tab') {
					e.preventDefault();
					$dateTo.get(0).focus();
				}
			});

			$dateTo.on('keydown', e => {
				if (e.shiftKey && e.key === 'Tab') {
					e.preventDefault();
					$dateFrom.get(0).focus();
				}
			});

			const fromPicker = this.createPicker(dateFromElement, $dateFrom, $hiddenDateFrom);
			const toPicker = this.createPicker($dateTo.get(0), $dateTo, $hiddenDateTo);

			$dateFrom.closest('[data-filter-component]').on('click', '[data-date-range-preset]', e => {
				const [start, end] = createPreset(String($(e.currentTarget).data('date-range-preset')));

				this.setDate(start, fromPicker, $hiddenDateFrom);
				this.setDate(end, toPicker, $hiddenDateTo);
			});
		});
	}

	private createPicker(element: HTMLElement, $input: JQuery, $hiddenInput: JQuery): any {
		const picker = new easepick.create({
			lang: (document.documentElement.lang || 'en').replace('_', '-'),
			format: this.dateFormatFE,
			element,
			readonly: false,
			autoApply: true,
			calendars: 1,
			css: getEntryPaths('daterangepicker', 'css'),
		});

		this.pickers.push(picker);

		$input.on('focus', () => {
			this.pickers.forEach(otherPicker => {
				if (otherPicker !== picker && otherPicker.isShown()) {
					otherPicker.hide();
				}
			});
			picker.show();
		});

		let pointerDownInPicker = false;
		picker.on('pointerdown', () => {
			pointerDownInPicker = true;
		});
		picker.on('pointercancel', () => {
			pointerDownInPicker = false;
		});
		picker.on('click', e => {
			pointerDownInPicker = false;
			e.stopPropagation();
		});
		picker.on('select', e => {
			const date = e.detail.date;
			$hiddenInput.val(date ? moment(date).format(this.dateFormatBE) : '');
		});

		$input.on('input', () => {
			const date = moment($input.val(), this.dateFormatFE, true);
			$hiddenInput.val(date.isValid() ? date.format(this.dateFormatBE) : '');
		});

		$input.on('blur', () => {
			const inputValue = String($input.val() ?? '');
			const date = moment(inputValue, this.dateFormatFE, true);
			$hiddenInput.val(date.isValid() ? date.format(this.dateFormatBE) : '');

			// Pointer interaction blurs the input before Easepick handles the click.
			// Rendering here would replace the clicked calendar element.
			if (pointerDownInPicker) {
				return;
			}

			if (date.isValid()) {
				picker.setDate(date.toDate());
			} else {
				picker.clear();
			}
		});

		$hiddenInput.on('change', () => {
			const date = moment($hiddenInput.val(), this.dateFormatBE, true);
			if (date.isValid()) {
				picker.setDate(date.toDate());
			} else {
				picker.clear();
			}
		});

		return picker;
	}

	private formatBackendDateForDisplay(value: string): string {
		const date = moment(value, this.dateFormatBE, true);

		return date.isValid() ? date.format(this.dateFormatFE) : '';
	}

	private setDate(date: moment.Moment, picker: any, $hiddenInput: JQuery): void {
		$hiddenInput.val(date.format(this.dateFormatBE));
		picker.setDate(date.toDate());
	}

}

function createPreset(preset: string): [moment.Moment, moment.Moment] {
	if (preset === 'lastWeek') {
		return [moment().subtract(1, 'week').startOf('isoWeek'), moment().subtract(1, 'week').endOf('isoWeek')];
	}

	if (preset === 'lastMonth') {
		return [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')];
	}

	return [moment().startOf('day'), moment().endOf('day')];
}
